/**
 * Pure helpers of the guided tours (organizer tours, 28 Oct 2026). A tour definition is
 * `{ id, version, steps[], paths? | routes? }` (`paths`: the routes an organizer page tour plays on,
 * relative to the event root; `routes`: absolute routes of a site tour — the home page, « My events » —
 * played by the site host outside the organizer shell); a step is a plain object:
 *  - `key`: stable name (analytics, tests)
 *  - `target`: a selector (`[data-tour="…"]`), null for a centred step
 *  - `title`, `description`: keys of the `tours` namespace; `vars(ctx)` feeds the interpolation
 *  - `placement` / `placementNarrow`: antd placements, the narrow one used on phones (< 700 px) when given
 *  - `desktopOnly` / `narrowOnly` / `when(ctx)`: when the step plays at all
 *  - `assumeTarget`: skip the on-screen check (a target the step's own `menu` hold will reveal, like the pin)
 *  - `menu: { hold: true, group? }`: keep the side menu unfolded, with that accordion group open
 *  - `section: '<tourKey>'`: open that OrganizerSection card of an accordion page while the step shows
 *    (the target is then the open card, or its band when the card is taller than the screen)
 *  - `block: '<band selector>'`: show the whole target block (a hub tab, a list card, a table): scrolled
 *    under the head, spotlighted entirely when it fits with its bubble, else only its band (the selector,
 *    queried inside the block) with the body visible below
 *  - `scroll: 'start'`: scroll the target under the sticky head before the step shows (a step whose
 *    bubble is tall — a select with a list of entries — needs the room below)
 *  - `navigate: '<path>'`: one of the tour's `paths` to show first (a hub tab, '' = the root); the host
 *    navigates there, waits for the target, and goes back where the tour started when it ends
 *  - `entriesDesktopOnly`: the entry lines are left out on a phone (< 700 px), where a tall bubble
 *    would not fit under its target
 *  - `enter()` / `leave()`: called when the step shows / is left (open the home advanced search panel,
 *    close it again); a step whose target `enter` reveals also sets `assumeTarget`
 */

/**
 * A `data-tour` value for a menu group key (same cleaning as `OrgMenuList`'s `osdGrp-` ids). The
 * « Event page » link group has the empty key (`item.to` = ''): it anchors as `menu-group-eventpage`.
 */
export const menuGroupAnchor = (groupKey) =>
    `menu-group-${String(groupKey ?? '').replace(/[^a-z0-9]/gi, '') || 'eventpage'}`

/** A step plays when its width / `when` gates pass and its target (if any) is on screen now. */
export const stepApplies = (step, ctx) => {
    if (step.desktopOnly && ctx.narrow) return false
    if (step.narrowOnly && !ctx.narrow) return false
    if (typeof step.when === 'function' && !step.when(ctx)) return false
    // a step that navigates first keeps its place: its target paints once the route shows
    const assume = step.assumeTarget || typeof step.navigate === 'string'
    if (step.target && !assume && typeof ctx.hasTarget === 'function' && !ctx.hasTarget(step.target)) {
        return false
    }
    return true
}

/** The steps of a definition that play in this context, in order (an empty list = nothing to show). */
export const resolveSteps = (def, ctx) => (def?.steps || []).filter((step) => stepApplies(step, ctx))

/**
 * The antd placement of a step: `placementNarrow` only on a phone-sized screen (no room beside the
 * 280-px drawer); a narrow desktop window (≤ 1100) keeps the regular placement — a bubble beside the
 * drawer always fits the height, one under a tall group (Contracts and its two sections) does not.
 */
export const stepPlacement = (step, phone) => (phone && step.placementNarrow) || step.placement || 'bottom'

/** The labels of the menu groups among `keys` that this viewer actually has, in menu order. */
export const presentGroupLabels = (groups, keys, label) =>
    (groups || []).filter((group) => keys.includes(group.key)).map((group) => label(group.title))

/** The translation namespaces a tour's entry labels borrow (deduplicated, definition order). */
export const entryNamespaces = (def) => {
    const seen = []
    ;(def?.steps || []).forEach((step) =>
        (step.entries || []).forEach((entry) => {
            if (entry.ns && !seen.includes(entry.ns)) seen.push(entry.ns)
        }),
    )
    return seen
}

/** `menuHold` value a step asks for (null = let the menu behave as usual). */
export const menuHoldOf = (step) => (step?.menu?.hold ? { group: step.menu.group ?? null } : null)

/** `tourKey` of the section card a step wants open (null = leave the page's accordion alone). */
export const sectionHoldOf = (step) => (typeof step?.section === 'string' && step.section) || null

/** The route (relative to the event root) a step asks to show first, or null. */
export const navigateOf = (step) => (typeof step?.navigate === 'string' ? cleanRel(step.navigate) : null)

const cleanRel = (rel) => String(rel || '').replace(/^\/+|\/+$/g, '')

/** Whether a relative path is one of the routes the tour plays on (a hub tab for the event-page tour). */
export const tourAllowsPath = (def, rel) => (def?.paths || []).some((path) => cleanRel(path) === cleanRel(rel))

/** 'site' for a tour on absolute `routes` (home, My events), 'organizer' for a page tour on `paths` and the dashboard. */
export const tourScope = (def) => (Array.isArray(def?.routes) ? 'site' : 'organizer')

/**
 * Whether the tour keeps playing at this location: a site tour compares the absolute pathname with its
 * `routes`, an organizer tour the path relative to the event root with its `paths` (the dashboard
 * tour, without `paths`, stops on any navigation).
 */
export const tourAllowsLocation = (def, { pathname, rel }) =>
    tourScope(def) === 'site'
        ? def.routes.some((route) => cleanRel(route) === cleanRel(pathname))
        : tourAllowsPath(def, rel)

/** Whether closing at `current` of `total` steps ends the tour (last step) or skips it. */
export const closeOutcome = (current, total) =>
    current >= total - 1 ? { skipped: false } : { skipped: true, lastStep: current }
